/* AdaptiveObjectiveLog · Founder Operating System · Batch P2.3 · Feb 2026
 * =========================================================================
 * Append-only lesson-objective evolution UI. Renders the audit trail
 * of what was actually taught vs. what was planned. Add-new-entry
 * form NEVER edits or deletes prior rows — the backend enforces this
 * (sole writer is `services.continuous_workflow.record_objective_adaptation`).
 *
 * Mounted inside Work Mode. Non-intrusive by default — a small
 * accordion that expands when the instructor wants it.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, History } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

function fmtTs(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export function AdaptiveObjectiveLog({ lessonId }) {
  const [items, setItems] = useState([]);
  const [state, setState] = useState("loading");
  const [open, setOpen] = useState(false);
  const [adapted, setAdapted] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!lessonId) { setState("error"); return; }
    try {
      const r = await axios.get(
        `${API}/api/lessons/${encodeURIComponent(lessonId)}/objective-adaptations`,
        { withCredentials: true },
      );
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      setState("ok");
    } catch {
      setState("error");
    }
  };

  useEffect(() => { load(); }, [lessonId]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const txt = adapted.trim();
    if (!txt || saving || !lessonId) return;
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/api/lessons/${encodeURIComponent(lessonId)}/objective-adaptations`,
        { adapted_objective: txt, reason: reason.trim() || null },
        { withCredentials: true },
      );
      if (r.data?.status === "recorded") {
        toast.success("Objective updated");
        setAdapted("");
        setReason("");
        await load();
      } else {
        toast.error("Couldn't save that objective");
      }
    } catch {
      toast.error("Couldn't save that objective");
    } finally {
      setSaving(false);
    }
  };

  if (!lessonId) return null;

  return (
    <section
      data-testid="adaptive-objective-log"
      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3"
    >
      <button
        type="button"
        data-testid="adaptive-objective-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300"
      >
        <span className="inline-flex items-center gap-1">
          <History className="w-3.5 h-3.5" />
          Objective adaptations
          {state === "ok" && items.length > 0 && (
            <span
              data-testid="adaptive-objective-count"
              className="ml-1 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-1.5"
            >
              {items.length}
            </span>
          )}
        </span>
        <span>{open ? "Hide" : "Show"}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {state === "loading" && (
            <p data-testid="adaptive-objective-loading" className="text-xs text-slate-500">
              Loading history…
            </p>
          )}
          {state === "error" && (
            <p data-testid="adaptive-objective-error" className="text-xs text-rose-600">
              Couldn&apos;t load the objective history.
            </p>
          )}
          {state === "ok" && items.length === 0 && (
            <p data-testid="adaptive-objective-empty" className="text-xs text-slate-400 italic">
              No adaptations recorded yet.
            </p>
          )}
          {state === "ok" && items.length > 0 && (
            <ul className="space-y-2" data-testid="adaptive-objective-list">
              {items.map((it) => (
                <li
                  key={it.id}
                  data-testid={`adaptive-objective-entry-${it.id}`}
                  className="rounded-lg border border-slate-200 dark:border-slate-800 p-2 text-xs"
                >
                  <p className="text-slate-500">{fmtTs(it.recorded_at)}</p>
                  {it.original_objective && (
                    <p
                      data-testid="adaptive-objective-original"
                      className="text-slate-500 line-through"
                    >
                      {it.original_objective}
                    </p>
                  )}
                  <p
                    data-testid="adaptive-objective-adapted"
                    className="font-semibold text-slate-900 dark:text-white"
                  >
                    {it.adapted_objective}
                  </p>
                  {it.reason && (
                    <p className="text-slate-500 mt-0.5">Reason: {it.reason}</p>
                  )}
                </li>
              ))}
            </ul>
          )}

          <form
            onSubmit={submit}
            data-testid="adaptive-objective-form"
            className="border-t border-slate-100 dark:border-slate-800 pt-3 space-y-2"
          >
            <input
              type="text"
              data-testid="adaptive-objective-adapted-input"
              value={adapted}
              onChange={(e) => setAdapted(e.target.value)}
              placeholder="New objective for this lesson…"
              className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-transparent px-2 py-1.5 text-sm outline-none"
              maxLength={240}
            />
            <input
              type="text"
              data-testid="adaptive-objective-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional)"
              className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-transparent px-2 py-1.5 text-xs outline-none"
              maxLength={240}
            />
            <button
              type="submit"
              data-testid="adaptive-objective-submit"
              disabled={!adapted.trim() || saving}
              className="inline-flex items-center gap-1 rounded-lg bg-slate-900 text-white px-3 py-1.5 text-xs font-semibold disabled:bg-slate-300"
            >
              <Plus className="w-3.5 h-3.5" />
              {saving ? "Saving…" : "Record adaptation"}
            </button>
          </form>
        </div>
      )}
    </section>
  );
}
